/* global chrome */
import { useContext, useEffect, useRef } from 'react';
import { GlobalContext } from '../api/GlobalContext';
import { AudioRecorder } from '../utils/recordUtils';

const AutoTrigger = ({ onNewVisitCreated }) => {
  const { apiClient } = useContext(GlobalContext);
  const audioRecorderRef = useRef(null);
  const currentConversationIDRef = useRef(null);

  useEffect(() => {
    const handleTriggerDetection = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const hash = window.location.hash;

      if (hash.startsWith('#autoRecord=')) {
        const conversationID = hash.split('=')[1];
        if (conversationID) {
          onNewVisitCreated && onNewVisitCreated(conversationID);
        }
        window.history.replaceState(
          null,
          null,
          window.location.pathname + window.location.search
        );
      } else if (hash.startsWith('#startRecording=')) {
        const conversationID = hash.split('=')[1];
        if (conversationID && apiClient) {
          await handleStartRecording(conversationID);
        }
        window.history.replaceState(
          null,
          null,
          window.location.pathname + window.location.search
        );
      } else if (hash.startsWith('#stopRecording=')) {
        const conversationID = hash.split('=')[1];
        if (conversationID) {
          try {
            await handleStopRecording(conversationID);
            window.close();
          } catch (error) {
            console.error('Hash-based stop recording failed:', error);
            setTimeout(() => {
              window.close();
            }, 2000);
          }
        }
        window.history.replaceState(
          null,
          null,
          window.location.pathname + window.location.search
        );
      }
    };

    const handleStartRecording = async (conversationID) => {
      try {
        if (!apiClient) {
          console.error('No apiClient available for recording');
          return;
        }
        const setStatus = (status) => {
          chrome.tabs.query({}, (tabs) => {
            tabs.forEach((tab) => {
              chrome.tabs
                .sendMessage(tab.id, {
                  type: 'RECORDING_STATUS',
                  status: status
                })
                .catch(() => {});
            });
          });
        };
        const refresh = () => {};
        try {
          audioRecorderRef.current = AudioRecorder(
            conversationID,
            apiClient,
            setStatus,
            refresh,
            0
          );
        } catch (error) {
          console.error('Error creating AudioRecorder:', error);
          throw error;
        }
        currentConversationIDRef.current = conversationID;
        try {
          if (
            audioRecorderRef.current &&
            audioRecorderRef.current.startButton
          ) {
            audioRecorderRef.current.startButton();
          } else {
            throw new Error('startButton method not available');
          }
        } catch (error) {
          console.error('Error calling startButton:', error);
          throw error;
        }
      } catch (error) {
        console.error('AutoTrigger error starting recording:', error);
        try {
          chrome.tabs.query({}, (tabs) => {
            tabs.forEach((tab) => {
              chrome.tabs
                .sendMessage(tab.id, {
                  type: 'RECORDING_STATUS',
                  status: 'Recording error: ' + error.message
                })
                .catch(() => {});
            });
          });
        } catch (statusError) {
          console.error('Error sending error status:', statusError);
        }
      }
    };

    const handleStopRecording = async (conversationID) => {
      return new Promise(async (resolve, reject) => {
        try {
          const sendStopStatus = (status) => {
            chrome.tabs.query({}, (tabs) => {
              tabs.forEach((tab) => {
                chrome.tabs
                  .sendMessage(tab.id, {
                    type: 'RECORDING_STATUS',
                    status: status
                  })
                  .catch(() => {});
              });
            });
          };
          if (
            audioRecorderRef.current &&
            currentConversationIDRef.current === conversationID
          ) {
            sendStopStatus('Stopping recording...');
            if (audioRecorderRef.current.stopButton) {
              try {
                await audioRecorderRef.current.stopButton();
                sendStopStatus('Audio upload in progress...');
                const audioRecorderForMonitoring = audioRecorderRef.current;
                const waitForUploadComplete = () => {
                  if (
                    !audioRecorderForMonitoring ||
                    !audioRecorderForMonitoring.getUploadQueueStatus
                  ) {
                    sendStopStatus('Recording complete!');
                    resolve();
                    return;
                  }
                  const uploadStatus =
                    audioRecorderForMonitoring.getUploadQueueStatus();
                  if (uploadStatus.isComplete) {
                    sendStopStatus('All recordings uploaded!');
                    audioRecorderRef.current = null;
                    currentConversationIDRef.current = null;
                    resolve();
                  } else {
                    if (uploadStatus.queueLength > 0) {
                      sendStopStatus(
                        `Uploading... (${uploadStatus.queueLength} remaining)`
                      );
                    } else if (uploadStatus.isProcessing) {
                      sendStopStatus('Processing final upload...');
                    }
                    setTimeout(waitForUploadComplete, 1000);
                  }
                };
                setTimeout(waitForUploadComplete, 1000);
              } catch (stopError) {
                console.error('Error in stopButton:', stopError);
                sendStopStatus('Error processing audio: ' + stopError.message);
                audioRecorderRef.current = null;
                currentConversationIDRef.current = null;
                reject(stopError);
              }
            } else {
              sendStopStatus(
                'Error stopping recording: stopButton not available'
              );
              audioRecorderRef.current = null;
              currentConversationIDRef.current = null;
              reject(new Error('stopButton method not available'));
            }
          } else {
            sendStopStatus('Error: No active recording found');
            reject(new Error('No active recording found'));
          }
        } catch (error) {
          console.error('AutoTrigger error stopping recording:', error);
          chrome.tabs.query({}, (tabs) => {
            tabs.forEach((tab) => {
              chrome.tabs
                .sendMessage(tab.id, {
                  type: 'RECORDING_STATUS',
                  status: 'Error stopping recording: ' + error.message
                })
                .catch(() => {});
            });
          });
          reject(error);
        }
      });
    };

    const messageListener = (request, sender, sendResponse) => {
      if (
        request.type === 'STOP_RECORDING_VIA_HASH' &&
        request.conversationID
      ) {
        handleStopRecording(request.conversationID)
          .then(() => {
            window.close();
          })
          .catch(() => {
            setTimeout(() => {
              window.close();
            }, 2000);
          });
        sendResponse({ status: 'STOP_RECORDING_RECEIVED' });
      }
    };

    const hasChromeRuntime = typeof chrome !== 'undefined' && chrome.runtime;
    if (hasChromeRuntime) {
      chrome.runtime.onMessage.addListener(messageListener);
    }
    handleTriggerDetection();
    const handleUrlChange = () => {
      handleTriggerDetection();
    };
    window.addEventListener('hashchange', handleUrlChange);
    window.addEventListener('popstate', handleUrlChange);
    return () => {
      window.removeEventListener('hashchange', handleUrlChange);
      window.removeEventListener('popstate', handleUrlChange);
      if (hasChromeRuntime) {
        chrome.runtime.onMessage.removeListener(messageListener);
      }
      if (audioRecorderRef.current) {
        audioRecorderRef.current.stopButton();
        audioRecorderRef.current = null;
      }
    };
  }, [apiClient, onNewVisitCreated]);
  return null;
};

export default AutoTrigger;
